import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Table } from '@tanstack/react-table';
import { useFilterPage, useProfileActions } from '../store/store';

interface TablePaginationProps<TData> {
  table: Table<TData>;
  totalCount: number;
}

export function TablePagination<TData>({ table, totalCount }: TablePaginationProps<TData>) {
  const { pageIndex: currentPage } = useFilterPage();
  const { setPage } = useProfileActions();

  const totalPages = Math.ceil(totalCount / table.getState().pagination.pageSize);
  const pageSize = table.getState().pagination.pageSize;
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 3;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pageNumbers.push(1, 2, 3);
        if (totalPages > 3) pageNumbers.push('...');
      } else if (currentPage >= totalPages - 1) {
        if (totalPages > 3) pageNumbers.push('...');
        pageNumbers.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pageNumbers.push('...', currentPage - 1, currentPage, currentPage + 1, '...');
      }
    }
    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-end space-x-2 py-4">
      <div className="flex-1 text-sm text-muted-foreground">
        Showing {pageSize} of {Intl.NumberFormat().format(totalCount)} records
      </div>
      <div className="space-x-2 flex sm:flex-wrap sm:gap-y-2 sm:space-x-0 text-foreground">
        <Button
          variant="secondary"
          size="sm"
          className="bg-transparent hover:bg-transparent border-transparent"
          onClick={() => {
            table.previousPage();
            setPage(currentPage - 1);
          }}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="ml-2 h-4 w-4" />
          Previous
        </Button>
        {pageNumbers.map((pgNumber, index) => (
          <span key={index}>
            {pgNumber === '...' ? (
              <span className="items-center px-3 py-1">...</span>
            ) : (
              <button
                className={`px-3 py-1 rounded text-foreground hover:bg-accent hover:border hover:border-border ${
                  currentPage === pgNumber
                    ? 'border border-border hover:border hover:border-accent-foreground'
                    : ''
                }`}
                onClick={() => {
                  if (typeof pgNumber === 'number') {
                    table.setPageIndex(pgNumber - 1);
                    setPage(pgNumber);
                  }
                }}
              >
                {pgNumber}
              </button>
            )}
          </span>
        ))}
        <Button
          variant="secondary"
          className="border-transparent bg-transparent hover:bg-transparent"
          size="sm"
          onClick={() => {
            table.nextPage();
            setPage(currentPage + 1);
          }}
          disabled={currentPage >= totalPages}
        >
          Next
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
